import { Datum } from "../../../components/docs/Datum.js";
import { note, p, table } from "../blocks.js";
import type { WikiTopic } from "../types.js";

export const howASignalIsMade: WikiTopic = {
  slug: "how-a-signal-is-made",
  title: "How a signal is made",
  summary: "Three jobs write rows, and one unique index decides which of them are new.",
  group: "concepts",
  sections: [
    {
      heading: "Three jobs write rows",
      code: "hourly-signals / signals / swing-signals",
      blocks: [
        p(
          <>
            Nothing writes to <Datum>signals</Datum> continuously. Three scheduled jobs do, and each
            one owns a distinct part of the day.
          </>,
        ),
        table(
          "The jobs that write signal rows",
          ["Job", "When it runs", "What it evaluates"],
          [
            [
              "hourly-signals",
              <Datum key="h">10:16 to 15:16 IST</Datum>,
              "Every hourly strategy plus the confluence scored from their votes, one closed bucket at a time",
            ],
            [
              "signals",
              <Datum key="d">20:15 IST</Datum>,
              "Factor rank crossings and the daily technicals, stamped at the 15:30 close",
            ],
            [
              "swing-signals",
              <Datum key="s">20:30 IST</Datum>,
              "The ICT swing setup over the whole universe, once per session",
            ],
            [
              "earnings-reaction-signals",
              <Datum key="e">20:45 IST</Datum>,
              "Results filings the market rewarded by 6% or more over two sessions, entered at the second close",
            ],
          ],
        ),
        p(
          <>
            The hourly runner fires at <Datum>:16</Datum> rather than on the hour because that
            minute is measured: a probe on 2026-08-28 found the closed <Datum>13:15</Datum> bucket
            present and final ten seconds after it closed, and cron is minute-granular, so{" "}
            <Datum>:16</Datum> is the earliest safe minute.
          </>,
        ),
        p(
          <>
            The two evening jobs run late because they wait on the pipeline in front of them.
            Scoring lands at <Datum>20:00</Datum>, so rank crossings cannot be computed before it;
            the swing job reads adjusted daily bars, so it must not run before the session's
            adjustments are materialised. Their rows still carry an <Datum>occurred_at</Datum> of
            the <Datum>15:30</Datum> close, because that is when the setup was true.
          </>,
        ),
      ],
    },
    {
      heading: "What a row carries",
      blocks: [
        p(
          <>
            Every row names the instrument and the moment: <Datum>exchange</Datum>,{" "}
            <Datum>symbol</Datum>, <Datum>signal_type</Datum>, and <Datum>session_date</Datum>.
            Alongside those sit <Datum>family</Datum>, the job that emitted it, and{" "}
            <Datum>severity</Datum>, which decides whether it can reach a phone.
          </>,
        ),
        p(
          <>
            The rest is a JSON <Datum>payload</Datum> whose shape is per-type. The emitters agree on
            the keys that mean the same thing everywhere, so an intraday row and a swing row compare
            like with like: <Datum>strategy</Datum>, <Datum>side</Datum>, <Datum>price</Datum>,{" "}
            <Datum>stopLoss</Datum>, <Datum>takeProfit</Datum>, and <Datum>magnitude</Datum>.
            Everything past that belongs to the one strategy that wrote it.
          </>,
        ),
        note(
          <>
            <Datum>magnitude</Datum> is strategy-relative. A volume multiple and a gap in ATR both
            land in that key, and comparing the two numbers means nothing.
          </>,
        ),
      ],
    },
    {
      heading: "One row per symbol per type per session",
      code: "signals_identity_idx",
      blocks: [
        p(
          <>
            <Datum>signals_identity_idx</Datum> is a unique index on{" "}
            <Datum>(exchange, symbol, signal_type, session_date)</Datum>, and every insert is{" "}
            <Datum>ON CONFLICT DO NOTHING</Datum>. Together they are the whole cooldown: re-run a
            bucket or re-run the evening job and it writes nothing, sends nothing.
          </>,
        ),
        p(
          <>
            There is no "has this already fired" read before the write. The database decides, which
            removes the race a read-then-write would open between the hourly runner and a manual
            re-run.
          </>,
        ),
        p(
          <>
            That constraint is why direction lives in the type name rather than in the payload.{" "}
            <Datum>ema_cross_bullish</Datum> and <Datum>ema_cross_bearish</Datum> are distinct
            types, so a symbol can cross up in the morning and back down in the afternoon and keep
            both rows. A single <Datum>ema_cross</Datum> type carrying a <Datum>side</Datum> field
            would have thrown the second one away.
          </>,
        ),
        p(
          <>
            Confluence escalation works the same way. Two strategies agreeing writes{" "}
            <Datum>confluence_bullish</Datum>; three agreeing later in the session writes{" "}
            <Datum>confluence_bullish_strong</Datum>, a separate type, because raising the severity
            on the existing row would reach nobody. That row was already delivered.
          </>,
        ),
      ],
    },
    {
      heading: "What a switched-off strategy does",
      blocks: [
        p(
          <>
            Turning a strategy off on the settings page stops the job from writing new rows and
            hides the rows it already wrote. The API resolves the disabled keys to their signal
            types on every request and excludes them from the feed, the symbol page, the intraday
            chart, and the detail route.
          </>,
        ),
        note(
          <>
            The switch is global, not per-account. <Datum>strategy_settings</Datum> carries no user
            id, so one reader flipping it changes what everyone sees. A type no catalogue entry owns
            is never hidden, which is why retired rows stay visible.
          </>,
        ),
      ],
    },
  ],
};
